// app/javascript/controllers/rsvp_guest_form_controller.js
import { Controller } from "@hotwired/stimulus"
import JustValidate from "just-validate"

const VENUE_GUEST_NAME_SELECTOR = "[data-rsvp-guest-name]"

export default class extends Controller {
    static targets = ["form"]

    connect() {
        if (!this.hasFormTarget) return

        this.registeredVenueGuestNameFields = new Set()

        this.validator = new JustValidate(this.formTarget, {
            validateBeforeSubmitting: true,
            lockForm: false,
            focusInvalidField: true,
            errorFieldCssClass: "is-invalid",
            errorLabelCssClass: "invalid-feedback d-block mt-1",
        })

        this.registerStaticFields()
        this.syncVenueGuestNameValidation()
        this.syncGuestNamesSummary()

        // Keep the compatibility hidden guest_names field current.
        this.formTarget.addEventListener("input", this.onFormInput)
    }

    disconnect() {
        this.formTarget?.removeEventListener("input", this.onFormInput)
        this.validator?.destroy()
        this.registeredVenueGuestNameFields?.clear()
    }

    registerStaticFields() {
        this.addFieldIfPresent('[name="email"]', [
            {
                rule: "required",
                errorMessage: "Email is required",
            },
            {
                rule: "email",
                errorMessage: "Enter a valid email",
            },
        ])

        this.addFieldIfPresent('[name="first_name"]', [
            {
                rule: "required",
                errorMessage: "Your first name is required",
            },
        ])

        this.addFieldIfPresent('[name="last_name"]', [
            {
                rule: "required",
                errorMessage: "Your last name is required",
            },
        ])

        /*
         * Legacy/non-venue RSVP:
         *
         * Validate guest_names only when it is a visible input or textarea.
         * This deliberately ignores:
         *
         *   <input type="hidden" name="guest_names">
         */
        const legacyGuestNamesField = this.formTarget.querySelector(
            '[name="guest_names"]:not([type="hidden"])'
        )

        if (legacyGuestNamesField) {
            this.validator.addField(legacyGuestNamesField, [
                {
                    rule: "required",
                    errorMessage: "Guest names are required",
                },
                {
                    validator: (value) => {
                        const normalizedValue = String(value).trim()

                        return /^[^,]+(,\s*[^,]+)*$/.test(normalizedValue)
                    },
                    errorMessage: "Use comma-separated names",
                },
            ])
        }

        this.addFieldIfPresent('[name="guests"]', [
            {
                validator: () => {
                    const value = Number.parseInt(
                        this.formTarget.querySelector('[name="guests"]')?.value || "0",
                        10
                    )

                    return (
                        Number.isFinite(value) &&
                        value >= 1 &&
                        value <= 99
                    )
                },
                errorMessage: "Please select the number of guests",
            },
        ])
    }

    /**
     * Register the currently rendered per-attendee name fields.
     *
     * The counter can add or remove attendee rows, so this method:
     * - removes validation for fields no longer in the DOM;
     * - adds validation for newly created fields;
     * - leaves already registered fields alone.
     */
    syncVenueGuestNameValidation() {
        if (!this.validator) return

        const currentFields = new Set(this.venueGuestNameFields)

        // Remove fields whose attendee rows were removed.
        Array.from(this.registeredVenueGuestNameFields).forEach((field) => {
            if (currentFields.has(field)) return

            this.validator.removeField(field)
            this.registeredVenueGuestNameFields.delete(field)
        })

        // Register newly rendered attendee-name fields.
        currentFields.forEach((field) => {
            if (this.registeredVenueGuestNameFields.has(field)) return

            this.validator.addField(field, [
                {
                    validator: (value) => {
                        return String(value).trim().length > 0
                    },
                    errorMessage: "Guest name is required",
                },
                {
                    validator: (value) => {
                        return String(value).trim().length <= 255
                    },
                    errorMessage: "Guest name must be 255 characters or fewer",
                },
            ])

            this.registeredVenueGuestNameFields.add(field)
        })
    }

    /**
     * Preserve guest_names as a summary for existing code and legacy database
     * behaviour, but do not use it as the source of venue validation.
     */
    syncGuestNamesSummary() {
        const summaryField = this.formTarget.querySelector(
            'input[type="hidden"][name="guest_names"]'
        )

        if (!summaryField) return

        const names = this.venueGuestNameFields
            .map((field) => field.value.trim())
            .filter(Boolean)

        summaryField.value = names.join(", ")
    }

    onFormInput = (event) => {
        if (!(event.target instanceof Element)) return
        if (!event.target.matches(VENUE_GUEST_NAME_SELECTOR)) return

        this.syncGuestNamesSummary()
    }

    async onSubmit(event) {
        event.preventDefault()

        if (!this.validator) {
            HTMLFormElement.prototype.submit.call(this.formTarget)
            return
        }

        /*
         * The guest counter may have added or removed rows since connect().
         * Synchronize JustValidate immediately before validating.
         */
        this.syncVenueGuestNameValidation()
        this.syncGuestNamesSummary()

        const valid = await this.validator.revalidate()

        if (!valid) return

        // Disable both the desktop and mobile submit buttons.
        this.formTarget
            .querySelectorAll('[type="submit"]')
            .forEach((button) => {
                button.disabled = true
            })

        // Avoid firing the Stimulus submit action a second time.
        HTMLFormElement.prototype.submit.call(this.formTarget)
    }

    addFieldIfPresent(selector, rules) {
        const field = this.formTarget.querySelector(selector)

        if (!field) return

        this.validator.addField(field, rules)
    }

    get venueGuestNameFields() {
        return Array.from(
            this.formTarget.querySelectorAll(VENUE_GUEST_NAME_SELECTOR)
        )
    }
}